/**
 * Emulator Test Page (No Auth Required)
 * For quick local testing without authentication
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { EmulatorCore } from '../emulator/EmulatorCore';
import { RenderEngine } from '../emulator/RenderEngine';
import { AudioEngine } from '../emulator/AudioEngine';
import { InputManager, loadKeyMapping } from '../emulator/InputManager';
import '../styles/emulator.css';

export default function EmulatorTest() {
  // Canvas refs
  const topCanvasRef = useRef<HTMLCanvasElement>(null);
  const bottomCanvasRef = useRef<HTMLCanvasElement>(null);

  // Emulator systems
  const [emulatorCore] = useState(() => new EmulatorCore());
  const [renderEngine, setRenderEngine] = useState<RenderEngine | null>(null);
  const [audioEngine] = useState(() => new AudioEngine());
  const [inputManager] = useState(() => new InputManager(loadKeyMapping()));

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [fps, setFps] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Animation frame
  const animationFrameRef = useRef<number | null>(null);
  const frameCountRef = useRef<number>(0);
  const fpsUpdateTimeRef = useRef<number>(0);

  /**
   * Initialize emulator systems
   */
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);

        await emulatorCore.initialize();
        await audioEngine.initialize();

        if (topCanvasRef.current && bottomCanvasRef.current) {
          const renderer = new RenderEngine({
            topCanvas: topCanvasRef.current,
            bottomCanvas: bottomCanvasRef.current,
            scale: 2,
            smoothing: false,
          });
          setRenderEngine(renderer);
          renderer.drawPlaceholder('Upload a ROM file to begin');
        }

        inputManager.initialize((button, pressed) => {
          emulatorCore.setButtonState(button, pressed);
        });

        console.log('✓ All emulator systems initialized');
      } catch (err) {
        console.error('Emulator initialization failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize emulator');
      } finally {
        setIsLoading(false);
      }
    };

    init();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      inputManager.destroy();
      audioEngine.destroy();
      emulatorCore.destroy();
    };
  }, [emulatorCore, audioEngine, inputManager]);

  /**
   * Main emulation loop
   */
  const emulationLoop = useCallback((timestamp: number) => {
    if (!emulatorCore.isReady() || !renderEngine) {
      return;
    }

    emulatorCore.runFrame();

    const topScreen = emulatorCore.getTopScreen();
    const bottomScreen = emulatorCore.getBottomScreen();
    if (topScreen || bottomScreen) {
      renderEngine.renderScreens(topScreen, bottomScreen);
    }

    const audioBuffer = emulatorCore.getAudioBuffer();
    if (audioBuffer) {
      audioEngine.processAudio(audioBuffer);
    }

    frameCountRef.current++;
    if (timestamp - fpsUpdateTimeRef.current >= 1000) {
      const currentFps = Math.round(
        (frameCountRef.current * 1000) / (timestamp - fpsUpdateTimeRef.current)
      );
      setFps(currentFps);
      frameCountRef.current = 0;
      fpsUpdateTimeRef.current = timestamp;
    }

    animationFrameRef.current = requestAnimationFrame(emulationLoop);
  }, [emulatorCore, renderEngine, audioEngine]);

  /**
   * Start emulation
   */
  const startEmulation = useCallback(() => {
    if (!emulatorCore.isReady()) {
      return;
    }

    setIsRunning(true);
    setIsPaused(false);
    emulatorCore.resume();

    fpsUpdateTimeRef.current = performance.now();
    frameCountRef.current = 0;

    animationFrameRef.current = requestAnimationFrame(emulationLoop);
  }, [emulatorCore, emulationLoop]);

  /**
   * Handle ROM upload
   */
  const handleROMUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      setError(null);

      console.log(`📂 Loading ROM file: ${file.name} (${file.size} bytes)`);

      const romBuffer = await file.arrayBuffer();

      console.log('📥 Loading ROM into melonDS...');
      await emulatorCore.loadROM(romBuffer);
      console.log('✅ ROM loaded successfully into emulator');

      renderEngine?.drawPlaceholder('Press START to begin');

      setTimeout(() => startEmulation(), 500);
    } catch (err) {
      console.error('❌ ROM loading failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ROM');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle pause
   */
  const handleTogglePause = () => {
    if (isPaused) {
      emulatorCore.resume();
      animationFrameRef.current = requestAnimationFrame(emulationLoop);
      setIsPaused(false);
    } else {
      emulatorCore.pause();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setIsPaused(true);
    }
  };

  return (
    <div className="emulator-page">
      <div className="emulator-container">
        <div className="emulator-header">
          <h1>🎮 Nintendo DS Emulator - Test Mode</h1>
          <p style={{ color: '#ff9800' }}>⚠️ No authentication - Local testing only</p>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="emulator-content">
          <div className="screens-container">
            <div className="screen-wrapper">
              <canvas ref={topCanvasRef} className="ds-screen top-screen" />
              <span className="screen-label">Top Screen</span>
            </div>

            <div className="screen-wrapper">
              <canvas ref={bottomCanvasRef} className="ds-screen bottom-screen" />
              <span className="screen-label">Touch Screen</span>
            </div>
          </div>

          <div className="controls-panel">
            <div className="control-group">
              <label htmlFor="rom-upload" className="upload-button">
                {isLoading ? '⏳ Loading...' : '📂 Upload ROM File'}
              </label>
              <input
                id="rom-upload"
                type="file"
                accept=".nds,.ds"
                onChange={handleROMUpload}
                disabled={isLoading}
                style={{ display: 'none' }}
              />
            </div>

            {isRunning && (
              <>
                <button onClick={handleTogglePause} className="control-button">
                  {isPaused ? '▶️ Resume' : '⏸️ Pause'}
                </button>
                <div className="fps-counter">
                  FPS: {fps}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="keyboard-guide">
          <h3>⌨️ Keyboard Controls</h3>
          <div className="key-mappings">
            <span>↑↓←→: D-Pad</span>
            <span>Z: A</span>
            <span>X: B</span>
            <span>A: Y</span>
            <span>S: X</span>
            <span>Q: L</span>
            <span>W: R</span>
            <span>Enter: Start</span>
            <span>Shift: Select</span>
          </div>
        </div>
      </div>
    </div>
  );
}
